import React, { useCallback, useEffect, useState } from "react";
import AppContainer from "../../common/app_container";
import DataTable from "react-data-table-component";
import { useNavigate } from "react-router-dom";
import Alert, { AlertProps } from "../../common/alert";
import _ from "lodash";
import { LicensableProperty } from "@src/model/licensable_property";
import { useLicensablePropertyAPI } from "@src/api/licensable_property_api";

const LicensablePropertyIndex: React.FC = () => {
    const [data, setData] = useState<LicensableProperty[]>([]);
    const [search, setSearch] = useState("");
    const [status, setStatus] = useState<AlertProps | null>(null);
    const [loading, setLoading] = useState<boolean>(true);
    const [totalRows, setTotalRows] = useState(0);
    const [currentPage, setCurrentPage] = useState(1);
    const [perPage, setPerPage] = useState(10);
    const { search: searchProperties } = useLicensablePropertyAPI();
    const navigate = useNavigate();

    const columns = [
        {
            name: "Name",
            selector: (row: LicensableProperty) => row.name || "N/A",
            sortable: true,
        },
        {
            name: "Description",
            selector: (row: LicensableProperty) => row.description || "N/A",
            sortable: true,
            wrap: true,
        },
        {
            name: "Gender",
            selector: (row: LicensableProperty) => row.gender || "N/A",
            sortable: true,
        },
        {
            name: "Actions",
            cell: (row: LicensableProperty) => (
                <button
                    onClick={() => navigate(`/licensable-properties/edit/${row.id}`)}
                    className="px-3 py-1 bg-blue-500 text-white text-xs font-bold rounded hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-opacity-50"
                >
                    Edit
                </button>
            ),
        },
    ];

    const fetchProperties = useCallback(
        async (searchTerm: string, page: number, count: number) => {
            setLoading(true);
            setStatus(null);
            try {
                const offset = (page - 1) * count;
                const response = await searchProperties(searchTerm, undefined, count, offset);
                setData(response);
                // Since search API might not return total count, we estimate or use length
                // Ideally API should return count. Assuming response is just array.
                if (offset === 0 && response.length < count) {
                    setTotalRows(response.length);
                } else if (response.length === 0 && page > 1) {
                    // Keep existing
                } else {
                    // Infinite scroll assumption or simple pagination hack if API doesn't give total
                    setTotalRows(offset + response.length + (response.length === count ? 1 : 0));
                }
            } catch (error: any) {
                setStatus({ type: "error", message: "Failed to load properties: " + error.message });
            } finally {
                setLoading(false);
            }
        },
        [searchProperties]
    );

    // Use useMemo to ensure the debounced function is created only once
    const debouncedSearch = React.useMemo(
        () =>
            _.debounce((term: string) => {
                setCurrentPage(1);
                fetchProperties(term, 1, perPage);
            }, 500),
        [fetchProperties, perPage]
    );

    useEffect(() => {
        fetchProperties(search, currentPage, perPage);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentPage, perPage]); // Adding dependencies, but careful about 'search' triggering loops if not handled

    const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const term = event.target.value;
        setSearch(term);
        debouncedSearch(term);
    };

    const handlePageChange = (page: number) => {
        setCurrentPage(page);
        fetchProperties(search, page, perPage);
    };

    const handlePerRowsChange = async (newPerPage: number, page: number) => {
        setPerPage(newPerPage);
        setCurrentPage(page);
        fetchProperties(search, page, newPerPage);
    };

    return (
        <AppContainer>
            <div className="md:p-6">
                <div className="flex justify-between items-center mb-4">
                    <h1 className="text-2xl font-semibold">Licensable Properties</h1>
                </div>

                {status && (
                    <div className="mb-4">
                        <Alert {...status} onClose={() => setStatus(null)} />
                    </div>
                )}
                <div className="mb-4 flex flex-row gap-4 justify-end">
                    <div className="relative w-full max-w-xs">
                        <label htmlFor="property-search" className="sr-only">
                            Search Properties
                        </label>
                        <div className="absolute inset-y-0 start-0 flex items-center ps-3 pointer-events-none">
                            <svg className="w-4 h-4 text-gray-400" aria-hidden="true" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 20 20">
                                <path stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="m19 19-4-4m0-7A7 7 0 1 1 1 8a7 7 0 0 1 14 0Z" />
                            </svg>
                        </div>
                        <input
                            type="search"
                            id="property-search"
                            value={search}
                            onChange={handleSearchChange}
                            className="block w-full p-2 ps-10 text-sm text-gray-900 border border-gray-300 rounded-lg bg-gray-50 focus:ring-blue-500 focus:border-blue-500"
                            placeholder="Search Name..."
                        />
                    </div>
                </div>

                <div className="border rounded shadow-sm">
                    <DataTable
                        columns={columns}
                        data={data}
                        progressPending={loading}
                        progressComponent={<div className="py-10 text-center">Loading...</div>}
                        noDataComponent={<div className="py-10 text-center text-gray-500">No properties found.</div>}
                        pagination
                        paginationServer
                        paginationTotalRows={totalRows}
                        onChangeRowsPerPage={handlePerRowsChange}
                        onChangePage={handlePageChange}
                        paginationDefaultPage={currentPage}
                        paginationPerPage={perPage}
                        paginationRowsPerPageOptions={[10, 20, 50, 100]}
                        highlightOnHover
                        striped
                        customStyles={{
                            headCells: {
                                style: { fontSize: "13px", fontWeight: "bold", color: "#4a5568", backgroundColor: "#f7fafc" },
                            },
                            cells: { style: { fontSize: "13px" } },
                        }}
                    />
                </div>
            </div>
        </AppContainer>
    );
};

export default LicensablePropertyIndex;
